import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Autocomplete,
  createFilterOptions,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog as SubDialog
} from '@mui/material';
import { 
  Close, 
  Save as SaveIcon, 
  Category as CategoryIcon, 
  Info as InfoIcon,
  ShoppingBag as ShoppingBagIcon,
  Language as LanguageIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Sync as SyncIcon,
  DynamicFeed as DynamicFeedIcon,
  CheckCircle,
  Cancel,
  Star,
  AutoGraph,
  Edit as EditIcon,
  Collections as CollectionsIcon
} from '@mui/icons-material';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from './Dexie';
import { log } from '../services/loggingService';
import { 
  fetchProductCategories, 
  addProductCategory, 
  deleteProductCategory,
  addCategory,
  syncProductVariants,
  addProdottoVariante,
  addProdottoAttributi,
  syncAttributes,
  editProdottoVariante,
  deleteProdottoVariante,
  syncCategories,
  generaCombinazioneProdottoAttributi,
  syncMedia,
  addMedia,
  deleteMedia
} from '../services/productService';

const filter = createFilterOptions();

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function ProductEditModal({ open, onClose, product, onSave, isSaving }) {
  const [editedProduct, setEditedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  
  // Categories management state
  const [isSyncingCategories, setIsSyncingCategories] = useState(false);
  const [categoryValue, setCategoryValue] = useState(null);
  
  // Variants management state
  const [isSyncingVariants, setIsSyncingVariants] = useState(false);
  const [isGeneratingVariants, setIsGeneratingVariants] = useState(false);
  const [isGeneratorOpen, setIsGeneratingOpen] = useState(false);
  const [selectedAttributeValues, setSelectedAttributeValues] = useState({}); // { attributo_id: [valore_id, ...] }
  const [activeAttributeIds, setActiveAttributeIds] = useState([]);
  
  // Single Variant Edit state
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const [isSavingVariant, setIsSavingVariant] = useState(false);
  
  // Media management state
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [isSyncingMedia, setIsSyncingMedia] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Get all available categories for the dropdown
  const allCategories = useLiveQuery(() => db.techyshop_categorie.toArray(), []);
  
  // Get product-category associations
  const productCategories = useLiveQuery(
    () => db.techyshop_prodotto_categoria.where('prodotto_id').equals(product?.id || 0).toArray(),
    [product?.id]
  );

  // Get product variants
  const productVariants = useLiveQuery(
    () => db.techyshop_prodotto_varianti.where('prodotto_id').equals(product?.id || 0).toArray(),
    [product?.id]
  );

  // Get product media
  const productMedia = useLiveQuery(
    () => db.techyshop_medias
      .where('tabella').equals('techyshop_prodotti')
      .and(m => m.element_id === (product?.id || 0))
      .toArray(),
    [product?.id]
  );

  // Get variant attribute associations
  const variantAttributes = useLiveQuery(
    () => db.techyshop_prodotto_attributo.where('prodotto_id').equals(product?.id || 0).toArray(),
    [product?.id]
  );

  // Attributes data
  const attributes = useLiveQuery(() => db.techyshop_attributi.toArray(), []);
  const attributeValues = useLiveQuery(() => db.techyshop_attributo_valori.toArray(), []);

  useEffect(() => {
    if (product) {
      setEditedProduct({
        ...product,
        id: product.id,
        sku: product.sku || '',
        nome: product.nome || '',
        slug: product.slug || '',
        descrizione_lunga: product.descrizione_lunga || '',
        descrizione_breve: product.descrizione_breve || '',
        prezzo: product.prezzo || 0,
        prezzo_scontato: product.prezzo_scontato || 0,
        in_sconto: product.in_sconto === 1 || product.in_sconto === true,
        quantita: product.quantita || 0,
        peso: product.peso || 0,
        altezza: product.altezza || 0,
        larghezza: product.larghezza || 0,
        profondita: product.profondita || 0,
        visibile: product.visibile === 1 || product.visibile === true,
        in_evidenza: product.in_evidenza === 1 || product.in_evidenza === true,
        meta_title: product.meta_title || '',
        meta_description: product.meta_description || '',
      });
      setActiveTab(0);
      
      // Auto-fetch data from server
      fetchProductCategories(product.id);
      syncProductVariants(product.id, false);
      syncAttributes();
      syncCategories();
    } else {
      setEditedProduct(null);
    }
    setIsDirty(false);
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    setEditedProduct(prev => ({ ...prev, [name]: finalValue }));
    setIsDirty(true);
  };

  const handleSave = () => {
    const normalized = {
      nome: editedProduct.nome,
      slug: editedProduct.slug,
      sku: editedProduct.sku,
      descrizione_breve: editedProduct.descrizione_breve,
      descrizione_lunga: editedProduct.descrizione_lunga,
      peso: parseFloat(editedProduct.peso) || 0,
      altezza: parseFloat(editedProduct.altezza) || 0,
      larghezza: parseFloat(editedProduct.larghezza) || 0,
      profondita: parseFloat(editedProduct.profondita) || 0,
      visibile: editedProduct.visibile ? 1 : 0,
      in_evidenza: editedProduct.in_evidenza ? 1 : 0,
      meta_title: editedProduct.meta_title || '',
      meta_description: editedProduct.meta_description || '',
      prezzo: parseFloat(editedProduct.prezzo) || 0,
      prezzo_scontato: parseFloat(editedProduct.prezzo_scontato) || 0,
      in_sconto: editedProduct.in_sconto ? 1 : 0,
      quantita: parseInt(editedProduct.quantita) || 0,
    };

    const updates = { id: product.id };
    let hasChanges = false;

    Object.keys(normalized).forEach(key => {
      if (normalized[key] !== product[key]) {
        updates[key] = normalized[key];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      onSave(updates);
    } else {
      onClose();
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // --- Category Actions ---
  const handleAddCategory = async () => {
    if (!categoryValue) return;
    let targetCatId = null;
    let targetCatName = "";

    if (typeof categoryValue === 'string') {
      targetCatName = categoryValue;
    } else if (categoryValue.inputValue) {
      targetCatName = categoryValue.inputValue;
    } else if (categoryValue.nome) {
      targetCatId = categoryValue.id;
      targetCatName = categoryValue.nome;
    }

    if (!targetCatId && targetCatName) {
      const cleanName = targetCatName.replace(/^Aggiungi "(.*)"$/, '$1');
      const existing = allCategories.find(c => c.nome.toLowerCase() === cleanName.toLowerCase());
      if (existing) {
        targetCatId = existing.id;
      } else {
        setToast({ open: true, message: `Creazione categoria "${cleanName}"...`, severity: 'info' });
        const createResult = await addCategory(cleanName);
        if (createResult.success) {
          targetCatId = createResult.category.id;
        } else {
          setToast({ open: true, message: `Errore creazione: ${createResult.message}`, severity: 'error' });
          return;
        }
      }
    }

    if (targetCatId) {
      const result = await addProductCategory(product.id, targetCatId);
      setToast({ open: true, message: result.message, severity: result.success ? 'success' : 'error' });
      if (result.success) {
        setCategoryValue(null);
        fetchProductCategories(product.id);
      }
    }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm("Rimuovere questa categoria dal prodotto?")) return;
    const result = await deleteProductCategory(product.id, catId);
    setToast({ open: true, message: result.message, severity: result.success ? 'info' : 'error' });
  };

  const handleSyncCategories = async () => {
    setIsSyncingCategories(true);
    const result = await fetchProductCategories(product.id);
    setToast({ open: true, message: result.success ? "Categorie sincronizzate!" : result.message, severity: result.success ? 'success' : 'error' });
    setIsSyncingCategories(false);
  };

  const handleSyncVariants = async () => {
    setIsSyncingVariants(true);
    const result = await syncProductVariants(product.id, true);
    setToast({ 
      open: true, 
      message: result.success ? "Varianti sincronizzate!" : result.message, 
      severity: result.success ? 'success' : 'error' 
    });
    setIsSyncingVariants(false);
  };

  // --- Media Actions ---
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;

    setIsUploadingMedia(true);
    let successCount = 0;
    let failCount = 0;

    for (const file of files) {
      const metadata = {
        tabella: 'techyshop_prodotti',
        element_id: product.id,
        principale: (productMedia?.length || 0) === 0 && successCount === 0 ? 1 : 0
      };

      const result = await addMedia(file, metadata);
      if (result.success) successCount++;
      else failCount++;
    }

    setToast({ 
      open: true, 
      message: `Caricamento completato: ${successCount} successo, ${failCount} fallimenti.`, 
      severity: failCount === 0 ? 'success' : 'warning' 
    });
    
    setIsUploadingMedia(false);
    syncMedia();
  };

  const handleUploadMedia = (e) => {
    uploadFiles(e.target.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadFiles(e.dataTransfer.files);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo media?")) return;
    const result = await deleteMedia(mediaId);
    setToast({ open: true, message: result.message, severity: result.success ? 'success' : 'error' });
    if (result.success) syncMedia();
  };

  const handleSyncMedia = async () => {
    setIsSyncingMedia(true);
    const result = await syncMedia();
    setToast({ open: true, message: result.success ? "Media sincronizzati!" : result.message, severity: result.success ? 'success' : 'error' });
    setIsSyncingMedia(false);
  };

  // --- Variant Generation Logic ---
  const handleToggleAttributeValue = (attrId, valId) => {
    setSelectedAttributeValues(prev => {
      const current = prev[attrId] || [];
      const updated = current.includes(valId) 
        ? current.filter(id => id !== valId)
        : [...current, valId];
      
      const newState = { ...prev };
      if (updated.length > 0) newState[attrId] = updated;
      else delete newState[attrId];
      return newState;
    });
  };

  const generateCombinations = () => {
    const keys = Object.keys(selectedAttributeValues);
    if (keys.length === 0) return [];

    const combine = (index) => {
      if (index === keys.length) return [[]];
      const attrId = keys[index];
      const vals = selectedAttributeValues[attrId];
      const result = [];
      const subCombos = combine(index + 1);
      for (const val of vals) {
        for (const sub of subCombos) {
          result.push([{ attributo_id: parseInt(attrId), attributo_valore_id: parseInt(val) }, ...sub]);
        }
      }
      return result;
    };

    return combine(0);
  };

  const handleGenerateVariants = async () => {
    const combinationsCount = generateCombinations().length;
    if (combinationsCount === 0) {
      alert("Seleziona almeno un valore per attributo.");
      return;
    }

    if (!window.confirm(`Stai per generare ${combinationsCount} varianti. Continuare?`)) return;

    setIsGeneratingVariants(true);

    try {
      const result = await generaCombinazioneProdottoAttributi(product.id, activeAttributeIds, selectedAttributeValues);
      
      setToast({ 
        open: true, 
        message: result.message, 
        severity: result.success ? 'success' : 'error' 
      });

      if (result.success) {
        setIsGeneratingOpen(false);
        setSelectedAttributeValues({});
        // Refresh variants list
        syncProductVariants(product.id, true);
      }
    } catch (error) {
      console.error("Errore generazione varianti:", error);
      setToast({ 
        open: true, 
        message: `Errore: ${error.message}`, 
        severity: 'error' 
      });
    } finally {
      setIsGeneratingVariants(false);
    }
  };

  // --- Single Variant Edit Logic ---
  const handleOpenVariantModal = (variant) => {
    setSelectedVariant({
      ...variant,
      visibile: variant.visibile === 1 || variant.visibile === true,
      default: variant.default === 1 || variant.default === true,
      in_sconto: variant.in_sconto === 1 || variant.in_sconto === true
    });
    setIsVariantModalOpen(true);
  };

  const handleVariantChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    setSelectedVariant(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleSaveVariant = async () => {
    setIsSavingVariant(true);
    const normalized = {
      sku: selectedVariant.sku,
      prezzo: parseFloat(selectedVariant.prezzo) || 0,
      prezzo_scontato: parseFloat(selectedVariant.prezzo_scontato) || 0,
      quantita: parseInt(selectedVariant.quantita) || 0,
      visibile: selectedVariant.visibile ? 1 : 0,
      default: selectedVariant.default ? 1 : 0,
      in_sconto: selectedVariant.in_sconto ? 1 : 0,
    };

    const original = productVariants.find(v => v.id === selectedVariant.id);
    const updates = { id: selectedVariant.id };
    let hasChanges = false;

    Object.keys(normalized).forEach(key => {
      if (normalized[key] !== original[key]) {
        updates[key] = normalized[key];
        hasChanges = true;
      }
    });

    if (!hasChanges) {
      setIsVariantModalOpen(false);
      setIsSavingVariant(false);
      return;
    }

    const result = await editProdottoVariante(updates);
    setToast({ 
      open: true, 
      message: result.message, 
      severity: result.success ? 'success' : 'error' 
    });

    if (result.success) {
      setIsVariantModalOpen(false);
      setSelectedVariant(null);
    }
    setIsSavingVariant(false);
  };

  const handleDeleteVariant = async (e, variantId) => {
    e.stopPropagation();
    if (!window.confirm("Sei sicuro di voler eliminare questa variante?")) return;
    
    try {
      // 1. Chiamata all'endpoint tramite il service
      const response = await deleteProdottoVariante(variantId);
      
      // 2. Mostriamo il messaggio ricevuto dal server (response.message contiene result.text)
      setToast({ 
        open: true, 
        message: response.message, 
        severity: response.success ? 'success' : 'error' 
      });

      // 3. Solo se l'operazione sul server ha avuto successo, aggiorniamo Dexie
      if (response.success) {
        // Elimina la variante da Dexie
        await db.techyshop_prodotto_varianti.delete(variantId);
        
        // Elimina a cascata le associazioni attributi della variante in Dexie
        await db.techyshop_prodotto_attributo.where('prodotto_variante_id').equals(variantId).delete();
        
        log(`Variante ID ${variantId} eliminata correttamente anche dal database locale.`, 'INFO');
      }
    } catch (error) {
      log("Errore critico in handleDeleteVariant:", 'ERROR', error);
      setToast({ 
        open: true, 
        message: `Errore durante l'eliminazione: ${error.message}`, 
        severity: 'error' 
      });
    }
  };

  const currentVariantAttributes = variantAttributes && attributes && attributeValues && selectedVariant
    ? variantAttributes
        .filter(va => va.prodotto_variante_id === selectedVariant.id)
        .map(va => {
          const attr = attributes.find(a => a.id === va.attributo_id);
          const val = attributeValues.find(v => v.id === va.attributo_valore_id);
          return {
            id: va.id,
            attributo: attr ? attr.nome : `Attr ID: ${va.attributo_id}`,
            valore: val ? val.valore : `Val ID: ${va.attributo_valore_id}`
          };
        })
    : [];

  const associatedCategories = productCategories && allCategories 
    ? productCategories.map(pc => {
        const cat = allCategories.find(c => c.id === pc.categoria_id);
        const parent = cat ? allCategories.find(p => p.id === cat.categoria_padre_id) : null;
        return { 
          id: pc.categoria_id, 
          nome: cat ? cat.nome : `ID: ${pc.categoria_id}`,
          slug: cat ? cat.slug : '',
          parentNome: parent ? parent.nome : '-'
        };
      })
    : [];

  if (!editedProduct) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullScreen
      PaperProps={{ sx: { backgroundColor: '#121212', backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ 
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)', px: 3, py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close"><Close /></IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Modifica Prodotto: {editedProduct.nome}</Typography>
          <Chip label={`ID: ${editedProduct.id}`} size="small" variant="outlined" sx={{ color: 'primary.main', borderColor: 'primary.main' }} />
        </Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!isDirty || isSaving}>
          {isSaving ? 'Salvataggio...' : 'Salva Modifiche'}
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="product tabs">
            <Tab icon={<InfoIcon />} iconPosition="start" label="Informazioni Prodotto" />
            <Tab icon={<ShoppingBagIcon />} iconPosition="start" label="Prezzi & Stock" />
            <Tab icon={<LanguageIcon />} iconPosition="start" label="SEO" />
            <Tab icon={<CategoryIcon />} iconPosition="start" label="Categorie" />
            <Tab icon={<DynamicFeedIcon />} iconPosition="start" label="Varianti" />
            <Tab icon={<CollectionsIcon />} iconPosition="start" label="Media" />
          </Tabs>
        </Box>

        <Box sx={{ px: 4, pb: 4 }}>
          <TabPanel value={activeTab} index={0}>
             <Grid container spacing={3}>
              <Grid item xs={12} md={6}><TextField fullWidth label="Nome Prodotto" name="nome" value={editedProduct.nome} onChange={handleChange} margin="normal" /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Slug" name="slug" value={editedProduct.slug} onChange={handleChange} margin="normal" /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="SKU" name="sku" value={editedProduct.sku} onChange={handleChange} margin="normal" /></Grid>
              
              <Grid item xs={12}>
                <TextField fullWidth label="Descrizione Breve" name="descrizione_breve" value={editedProduct.descrizione_breve} onChange={handleChange} margin="normal" multiline rows={3} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Descrizione Lunga" name="descrizione_lunga" value={editedProduct.descrizione_lunga} onChange={handleChange} margin="normal" multiline rows={8} />
              </Grid>

              <Grid item xs={12} md={3}><TextField fullWidth label="Peso (kg)" name="peso" type="number" value={editedProduct.peso} onChange={handleChange} margin="normal" /></Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label="Altezza (cm)" name="altezza" type="number" value={editedProduct.altezza} onChange={handleChange} margin="normal" /></Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label="Larghezza (cm)" name="larghezza" type="number" value={editedProduct.larghezza} onChange={handleChange} margin="normal" /></Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label="Profondità (cm)" name="profondita" type="number" value={editedProduct.profondita} onChange={handleChange} margin="normal" /></Grid>
              <Grid item xs={12} md={6}><FormControlLabel control={<Checkbox name="visibile" checked={editedProduct.visibile} onChange={handleChange} />} label="Visibile" /></Grid>
              <Grid item xs={12} md={6}><FormControlLabel control={<Checkbox name="in_evidenza" checked={editedProduct.in_evidenza} onChange={handleChange} />} label="In Evidenza" /></Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}><TextField fullWidth label="Prezzo" name="prezzo" type="number" value={editedProduct.prezzo} onChange={handleChange} margin="normal" /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Prezzo Scontato" name="prezzo_scontato" type="number" value={editedProduct.prezzo_scontato} onChange={handleChange} margin="normal" /></Grid>
              <Grid item xs={12} md={4}><FormControlLabel sx={{ mt: 3, ml: 1 }} control={<Checkbox name="in_sconto" checked={editedProduct.in_sconto} onChange={handleChange} />} label="In Sconto" /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Quantità" name="quantita" type="number" value={editedProduct.quantita} onChange={handleChange} margin="normal" /></Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <TextField fullWidth label="Meta Title" name="meta_title" value={editedProduct.meta_title} onChange={handleChange} margin="normal" />
            <TextField fullWidth label="Meta Description" name="meta_description" value={editedProduct.meta_description} onChange={handleChange} margin="normal" multiline rows={4} />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Associazione Categorie</Typography>
              <Button startIcon={<SyncIcon />} onClick={handleSyncCategories} disabled={isSyncingCategories} size="small">Reset e Sincronizza</Button>
            </Box>
            <Box sx={{ mb: 4, p: 2, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Autocomplete
                fullWidth size="small" value={categoryValue}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') setCategoryValue({ nome: newValue });
                  else if (newValue && newValue.inputValue) setCategoryValue({ nome: newValue.inputValue, inputValue: newValue.inputValue });
                  else setCategoryValue(newValue);
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);
                  const { inputValue } = params;
                  const isExisting = options.some((option) => inputValue === option.nome);
                  if (inputValue !== '' && !isExisting) filtered.push({ inputValue, nome: `Aggiungi "${inputValue}"` });
                  return filtered;
                }}
                selectOnFocus clearOnBlur handleHomeEndKeys options={allCategories || []}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.inputValue) return option.inputValue;
                  return option.nome || "";
                }}
                renderOption={(props, option) => <li {...props}>{option.nome}</li>}
                freeSolo renderInput={(params) => <TextField {...params} label="Cerca o crea categoria" />}
              />
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddCategory} disabled={!categoryValue} sx={{ height: 40 }}>Aggiungi</Button>
            </Box>
            <TableContainer component={Paper} sx={{ bgcolor: 'transparent', border: '1px solid rgba(255,255,255,0.1)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.02)' }}>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>ID</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Nome Categoria</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Slug</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Genitore</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {associatedCategories.map((cat) => (
                    <TableRow key={cat.id} hover>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.5)' }}>{cat.id}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{cat.nome}</TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{cat.slug}</TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{cat.parentNome}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Rimuovi associazione">
                          <IconButton size="small" color="error" onClick={() => handleDeleteCategory(cat.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {associatedCategories.length === 0 && <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nessuna categoria associata.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Varianti Prodotto</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  startIcon={<AutoGraph />} 
                  variant="outlined" 
                  color="secondary" 
                  size="small"
                  onClick={() => {
                    setActiveAttributeIds([]);
                    setSelectedAttributeValues({});
                    setIsGeneratingOpen(true);
                  }}
                >
                  Genera Combinazioni
                </Button>
                <Button startIcon={<SyncIcon />} onClick={handleSyncVariants} disabled={isSyncingVariants} size="small">Reset e Sincronizza</Button>
              </Box>
            </Box>
            <TableContainer component={Paper} sx={{ bgcolor: 'transparent', border: '1px solid rgba(255,255,255,0.1)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.02)' }}>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>ID</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>SKU</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Prezzo</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="center">Stock</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="center">Stato</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productVariants?.map((v) => (
                    <TableRow 
                      key={v.id} 
                      hover 
                      onClick={() => handleOpenVariantModal(v)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}
                    >
                      <TableCell sx={{ color: 'rgba(255,255,255,0.5)' }}>{v.id}</TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {v.sku}
                          {v.default === 1 && (
                            <Tooltip title="Variante Default">
                              <Star sx={{ color: '#ffc107', fontSize: 16 }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {v.in_sconto === 1 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography variant="caption" sx={{ textDecoration: 'line-through', opacity: 0.5 }}>€{v.prezzo}</Typography>
                            <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 'bold' }}>€{v.prezzo_scontato}</Typography>
                          </Box>
                        ) : `€${v.prezzo}`}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={v.quantita} size="small" color={v.quantita > 0 ? "success" : "error"} variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        {v.visibile === 1 ? <CheckCircle style={{ color: '#4dab56' }} fontSize="small" /> : <Cancel style={{ color: '#f44336' }} fontSize="small" />}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton size="small" color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <Tooltip title="Elimina variante">
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={(e) => handleDeleteVariant(e, v.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!productVariants || productVariants.length === 0) && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nessuna variante trovata.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={activeTab} index={5}>
            <Box 
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{ 
                minHeight: '400px',
                borderRadius: 2,
                transition: 'all 0.2s',
                position: 'relative',
                border: isDragging ? '2px dashed #1976d2' : '2px dashed transparent',
                bgcolor: isDragging ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
                p: 2
              }}
            >
              {isDragging && (
                <Box sx={{ 
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', 
                  zIndex: 10, display: 'flex', flexDirection: 'column', 
                  alignItems: 'center', justifyContent: 'center', 
                  bgcolor: 'rgba(18, 18, 18, 0.8)', borderRadius: 2,
                  pointerEvents: 'none'
                }}>
                  <AddIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" color="primary">Rilascia per caricare</Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Media Prodotto</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    component="label" 
                    variant="contained" 
                    startIcon={isUploadingMedia ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                    disabled={isUploadingMedia}
                  >
                    {isUploadingMedia ? 'Caricamento...' : 'Carica Media'}
                    <input type="file" hidden multiple accept="image/*" onChange={handleUploadMedia} />
                  </Button>
                  <Button startIcon={<SyncIcon />} onClick={handleSyncMedia} disabled={isSyncingMedia} size="small">Reset e Sincronizza</Button>
                </Box>
              </Box>

              <Grid container spacing={2}>
                {productMedia?.map((media) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={media.id}>
                    <Paper sx={{ 
                      position: 'relative', 
                      paddingTop: '100%', 
                      overflow: 'hidden', 
                      bgcolor: 'rgba(255,255,255,0.05)',
                      border: media.principale === 1 ? '2px solid #1976d2' : '1px solid rgba(255,255,255,0.1)',
                      '&:hover .media-actions': { opacity: 1 }
                    }}>
                      <img 
                        src={`https://techyshop.techysumo.it${media.url}`} 
                        alt={media.nome} 
                        style={{ 
                          position: 'absolute', 
                          top: 0, left: 0, 
                          width: '100%', height: '100%', 
                          objectFit: 'cover' 
                        }} 
                      />
                      {media.principale === 1 && (
                        <Chip 
                          label="Principale" 
                          size="small" 
                          color="primary" 
                          sx={{ position: 'absolute', top: 5, left: 5, height: 20, fontSize: '0.65rem' }} 
                        />
                      )}
                      <Box className="media-actions" sx={{ 
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', 
                        bgcolor: 'rgba(0,0,0,0.5)', opacity: 0, transition: 'opacity 0.2s',
                        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1
                      }}>
                        <IconButton size="small" color="error" onClick={() => handleDeleteMedia(media.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Paper>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.6 }}>
                      {media.nome}
                    </Typography>
                  </Grid>
                ))}
                {(!productMedia || productMedia.length === 0) && (
                  <Grid item xs={12}>
                    <Box sx={{ py: 8, textAlign: 'center', border: '2px dashed rgba(255,255,255,0.1)', borderRadius: 2 }}>
                      <CollectionsIcon sx={{ fontSize: 48, mb: 2, opacity: 0.2 }} />
                      <Typography color="text.secondary">Nessun media associato a questo prodotto.</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Trascina qui le immagini per caricarle
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Button onClick={onClose}>Chiudi</Button>
        <Button variant="contained" onClick={handleSave} disabled={!isDirty || isSaving}>
          {isSaving ? 'Salvataggio...' : 'Salva Modifiche'}
        </Button>
      </DialogActions>

      {/* Variant Generator Modal */}
      <SubDialog 
        open={isGeneratorOpen} 
        onClose={() => !isGeneratingVariants && setIsGeneratingOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#1e1e1e' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Generatore Combinazioni Varianti</Typography>
          <IconButton onClick={() => setIsGeneratingOpen(false)} disabled={isGeneratingVariants}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Scegli gli attributi da utilizzare per la generazione e seleziona i relativi valori.
          </Typography>
          
          <Autocomplete
            multiple
            id="attribute-selector"
            options={attributes || []}
            getOptionLabel={(option) => option.nome}
            value={attributes?.filter(a => activeAttributeIds.includes(a.id)) || []}
            onChange={(event, newValue) => {
              const newIds = newValue.map(a => a.id);
              setActiveAttributeIds(newIds);
              // Clean up selected values for removed attributes
              setSelectedAttributeValues(prev => {
                const updated = { ...prev };
                Object.keys(updated).forEach(id => {
                  if (!newIds.includes(parseInt(id))) {
                    delete updated[id];
                  }
                });
                return updated;
              });
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="Seleziona Attributi" placeholder="Cerca attributi..." />
            )}
            sx={{ mb: 4 }}
          />

          <Grid container spacing={3}>
            {attributes?.filter(a => activeAttributeIds.includes(a.id)).map(attr => (
              <Grid item xs={12} key={attr.id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    {attr.nome}
                  </Typography>
                  <Box>
                    <Button size="small" sx={{ textTransform: 'none' }} onClick={() => {
                      const allVals = attributeValues?.filter(v => v.attributo_id === attr.id).map(v => v.id);
                      setSelectedAttributeValues(prev => ({ ...prev, [attr.id]: allVals }));
                    }}>Seleziona Tutti</Button>
                    <Button size="small" color="error" sx={{ textTransform: 'none' }} onClick={() => {
                      setSelectedAttributeValues(prev => {
                        const updated = { ...prev };
                        delete updated[attr.id];
                        return updated;
                      });
                    }}>Deseleziona</Button>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {attributeValues?.filter(v => v.attributo_id === attr.id).map(val => (
                    <Chip 
                      key={val.id} 
                      label={val.valore} 
                      onClick={() => handleToggleAttributeValue(attr.id, val.id)}
                      color={selectedAttributeValues[attr.id]?.includes(val.id) ? "primary" : "default"}
                      variant={selectedAttributeValues[attr.id]?.includes(val.id) ? "filled" : "outlined"}
                      size="small"
                    />
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 4, p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Totale combinazioni: {generateCombinations().length}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsGeneratingOpen(false)} disabled={isGeneratingVariants}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={handleGenerateVariants} 
            disabled={isGeneratingVariants || generateCombinations().length === 0}
            startIcon={isGeneratingVariants ? <CircularProgress size={20} color="inherit" /> : <AutoGraph />}
          >
            {isGeneratingVariants ? 'Generazione in corso...' : 'Genera Varianti'}
          </Button>
        </DialogActions>
      </SubDialog>

      {/* Single Variant Edit Modal */}
      <SubDialog 
        open={isVariantModalOpen} 
        onClose={() => !isSavingVariant && setIsVariantModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#1e1e1e' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Modifica Variante ID: {selectedVariant?.id}</Typography>
          <IconButton onClick={() => setIsVariantModalOpen(false)} disabled={isSavingVariant}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          {selectedVariant && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth label="SKU" name="sku" 
                  value={selectedVariant.sku || ''} onChange={handleVariantChange} 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth label="ID Prodotto" value={selectedVariant.prodotto_id} 
                  disabled InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth label="Prezzo" name="prezzo" type="number" 
                  value={selectedVariant.prezzo} onChange={handleVariantChange} 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth label="Prezzo Scontato" name="prezzo_scontato" type="number" 
                  value={selectedVariant.prezzo_scontato} onChange={handleVariantChange} 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth label="Quantità" name="quantita" type="number" 
                  value={selectedVariant.quantita} onChange={handleVariantChange} 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel 
                  control={<Checkbox name="in_sconto" checked={selectedVariant.in_sconto} onChange={handleVariantChange} />} 
                  label="In Sconto" 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel 
                  control={<Checkbox name="visibile" checked={selectedVariant.visibile} onChange={handleVariantChange} />} 
                  label="Visibile" 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel 
                  control={<Checkbox name="default" checked={selectedVariant.default} onChange={handleVariantChange} />} 
                  label="Variante Default" 
                />
              </Grid>

              {/* Display associated attributes */}
              <Grid item xs={12}>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, border: '1px solid rgba(255,255,255,0.1)' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon fontSize="small" /> Attributi Associati
                  </Typography>
                  {currentVariantAttributes.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {currentVariantAttributes.map((attr) => (
                        <Chip 
                          key={attr.id} 
                          label={`${attr.attributo}: ${attr.valore}`} 
                          size="small" 
                          variant="outlined" 
                          sx={{ borderColor: 'rgba(255,255,255,0.2)' }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Nessun attributo associato a questa variante.</Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsVariantModalOpen(false)} disabled={isSavingVariant}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveVariant} 
            disabled={isSavingVariant}
            startIcon={isSavingVariant ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          >
            {isSavingVariant ? 'Salvataggio...' : 'Salva Variante'}
          </Button>
        </DialogActions>
      </SubDialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} variant="filled">{toast.message}</Alert>
      </Snackbar>
    </Dialog>
  );
}

export default ProductEditModal;
